import { useEffect, useState, useRef } from 'react';
import { createSearchParams, useNavigate } from 'react-router';

import SearchBar from '../../components/searchBar';
import UserClientCards from './components/UserClientCards';

import { VegaChip, VegaFlex, VegaFont, VegaPageNotification } from '@globalpayments/vega-react';
import { failure, unauthorized } from '../../types/api';
import { Errors } from '../../types/errors';

import apiFunctions from '../../api';
import PageSkeleton from '../../components/pageSkeleton';
import { UserClientCardProps } from './components/UserClientCardProps';

const UserClientLookup = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [cards, setCards] = useState<UserClientCardProps[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let subscribed = true;

    const backSearch = sessionStorage.getItem('searchVal');
    if (backSearch) {
      setHasTyped(true);
      setSearchValue(backSearch);
      sessionStorage.removeItem('searchVal');
    }

    const searchValues = async (searchValue: string) => {
      if (searchValue === '' || searchValue === undefined || searchValue === null) {
        clearCards();
        setLoading(false);
      } else {
        await apiFunctions.apiPost('Kirby/UserClient/GetUserClients', { SearchText: searchValue }).then((data) => {
          if (data === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.CallFailed })}`,
            });
          } else if (data === unauthorized) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.Unauthorized })}`,
            });
          }

          if (typeof data === 'string') {
            clearCards();
          } else {
            setCards(data);
          }
          setHasSearched(true);
          setLoading(false);
        });
      }
    };

    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }
    if (hasTyped) {
      timerRef.current = setTimeout(async () => {
        if (subscribed) {
          setHasTyped(false);
          setLoading(true);
          clearCards();
          searchValues(searchValue);
        }
      }, 400);
    }

    return () => {
      subscribed = false;
    };
  }, [hasTyped, navigate, searchValue]);

  const clearCards = () => {
    setCards([]);
  };

  return (
    <VegaFlex direction="col" gap="size-32">
      <SearchBar
        searchHeader="Search user clients"
        searchLabel="User client UID, email, company, application UID, AAID, or PURL ID"
        searchTooltipText="Enter a valid user client UID, email, DBA or company name, application UID, AAID, or PURL ID."
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        clearCards={clearCards}
        setHasTyped={setHasTyped}
      />

      {loading ? (
        <PageSkeleton label="Loading user client results" variant="results" />
      ) : cards.length > 0 ? (
        <VegaFlex direction="col" gap="size-16">
          <VegaFlex alignItems="center" flexWrap="wrap" gap="size-8" justifyContent="space-between">
            <VegaFont as="h2" variant="font-h5">
              Results for “{searchValue}”
            </VegaFont>
            <VegaChip
              chipType="label"
              size="small"
              text={`${cards.length} ${cards.length === 1 ? 'result' : 'results'}`}
              variant="outlined"
            />
          </VegaFlex>
          <UserClientCards cards={cards} />
        </VegaFlex>
      ) : hasSearched && searchValue ? (
        <VegaPageNotification
          duration={0}
          message={`No user clients matched “${searchValue}”. Try another identifier or name.`}
          notificationTitle="No user clients found"
          type="info"
        />
      ) : null}
    </VegaFlex>
  );
};

export default UserClientLookup;
